/**
 * HTTP transport — the production implementation of the API contract.
 *
 * Two rules hold this file together:
 *
 *  1. `Contract['x']` types every export against the mock. If the two ever
 *     drift, `tsc` fails here rather than in a component at runtime. The
 *     import is type-only, so nothing from the mock (or its 52-building seed)
 *     reaches the production bundle.
 *
 *  2. `requireBuilding` stays even though the server re-checks everything. It
 *     costs nothing and stops the UI from issuing a cross-tenant request at
 *     all, which keeps a whole class of bug out of the server logs.
 */
import { assertBuildingAccess } from './authz';
import { http } from './client';
import type {
  Attachment,
  AttachmentEntity,
  PageQuery,
  Role,
  SubscriptionPlan,
  TicketStatus,
} from './types';

type Contract = typeof import('./mock');

/** Same guard the mock uses, so both transports refuse the same requests. */
function requireBuilding(buildingId: number): void {
  assertBuildingAccess(buildingId);
}

/** Drops undefined keys so axios never serialises `?page=undefined`. */
function params(query: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(query).filter(([, value]) => value !== undefined));
}

function paging(page?: PageQuery): Record<string, unknown> {
  return { page: page?.page, pageSize: page?.pageSize };
}

/* ------------------------------------------------------------------- auth */

export const authApi: Contract['authApi'] = {
  async requestOtp(phone, channel = 'whatsapp') {
    await http.post('/auth/request-otp', { phone, channel });
  },

  async verifyOtp(phone, code, channel = 'whatsapp') {
    void channel;
    // The session arrives as a Set-Cookie header; the body carries no token.
    const { data } = await http.post('/auth/verify-otp', { phone, code });
    return data;
  },

  async me() {
    const { data } = await http.get('/auth/me');
    return data;
  },

  async logout() {
    await http.post('/auth/logout');
  },
};

/* -------------------------------------------------------------- buildings */

export const buildingsApi: Contract['buildingsApi'] = {
  async mine(period) {
    const { data } = await http.get('/buildings/mine', {
      params: params({ year: period?.year, month: period?.month }),
    });
    return data;
  },
};

/* ---------------------------------------------------------- admin console */

export const adminApi: Contract['adminApi'] = {
  async stats() {
    const { data } = await http.get('/admin/stats');
    return data;
  },

  async buildings(search: string, page?: PageQuery) {
    const { data } = await http.get('/admin/buildings', {
      params: params({ search, ...paging(page) }),
    });
    return data;
  },

  async users(search: string, role?: Role, page?: PageQuery) {
    const { data } = await http.get('/admin/users', {
      params: params({ search, role, ...paging(page) }),
    });
    return data;
  },

  async updateBuilding(buildingId, body) {
    const { data } = await http.put(`/admin/buildings/${buildingId}`, body);
    return data;
  },

  async purgeBuilding(buildingId, confirmName, purgeUsers = false) {
    const { data } = await http.post(`/admin/buildings/${buildingId}/purge`, { confirmName, purgeUsers });
    return data;
  },

  async purgeUser(userId, confirmPhone) {
    const { data } = await http.post(`/admin/users/${userId}/purge`, { confirmPhone });
    return data;
  },
};

/* -------------------------------------------------------------- dashboard */

export const dashboardApi: Contract['dashboardApi'] = {
  async summary(buildingId, period) {
    requireBuilding(buildingId);
    const { data } = await http.get(`/buildings/${buildingId}/dashboard`, {
      params: params({ year: period.year, month: period.month }),
    });
    return data;
  },
};

/* ------------------------------------------------------------------ units */

export const unitsApi: Contract['unitsApi'] = {
  async search(buildingId, search, page) {
    requireBuilding(buildingId);
    const { data } = await http.get(`/buildings/${buildingId}/units`, {
      params: params({ search, ...paging(page) }),
    });
    return data;
  },

  async lookups() {
    const { data } = await http.get('/lookups/occupancy-types');
    return data;
  },

  async getById(buildingId, id) {
    requireBuilding(buildingId);
    const { data } = await http.get(`/buildings/${buildingId}/units/${id}`);
    return data;
  },

  async create(buildingId, body) {
    requireBuilding(buildingId);
    const { data } = await http.post(`/buildings/${buildingId}/units`, body);
    return data;
  },

  async update(buildingId, id, body) {
    requireBuilding(buildingId);
    const { data } = await http.put(`/buildings/${buildingId}/units/${id}`, body);
    return data;
  },

  // Retire, never DELETE: the unit owns payment history.
  async deactivate(buildingId, id) {
    requireBuilding(buildingId);
    await http.post(`/buildings/${buildingId}/units/${id}/deactivate`);
  },

  async reactivate(buildingId, id) {
    requireBuilding(buildingId);
    await http.post(`/buildings/${buildingId}/units/${id}/reactivate`);
  },
};

/* --------------------------------------------------------------- payments */

export const paymentsApi: Contract['paymentsApi'] = {
  async list(buildingId, year, month, page) {
    requireBuilding(buildingId);
    const { data } = await http.get(`/buildings/${buildingId}/payments`, {
      params: params({ year, month, ...paging(page) }),
    });
    return data;
  },

  // يملأ شهراً لم تولّده المهمة المجدولة بعد. idempotent على الخادم، فزرٌّ
  // يُضغط مرتين لا ينتج صفوفاً مكرّرة.
  async generate(buildingId, year, month) {
    requireBuilding(buildingId);
    const { data } = await http.post(`/buildings/${buildingId}/payments/generate`, null, {
      params: params({ year, month }),
    });
    return data;
  },

  // يولّد الأشهر القادمة لهذه الشقة ثم يعيد كل غير المسدَّد. بلا هذا
  // النداء لا وجود للأشهر القادمة أصلاً، فلا شيء يُدفع مقدَّماً.
  async schedule(buildingId, unitId, months) {
    requireBuilding(buildingId);
    const { data } = await http.get(
      `/buildings/${buildingId}/payments/unit/${unitId}/schedule`,
      { params: params({ months }) },
    );
    return data;
  },

  async calendar(buildingId, year) {
    requireBuilding(buildingId);
    const { data } = await http.get(`/buildings/${buildingId}/payments/calendar`, {
      params: params({ year }),
    });
    return data;
  },

  async fund(buildingId) {
    requireBuilding(buildingId);
    const { data } = await http.get(`/buildings/${buildingId}/payments/fund`);
    return data;
  },

  async setOpening(buildingId, amount, note) {
    requireBuilding(buildingId);
    const { data } = await http.put(`/buildings/${buildingId}/payments/fund/opening`, {
      amount,
      note,
    });
    return data;
  },

  async setEmergencyOpening(buildingId, amount, note) {
    requireBuilding(buildingId);
    const { data } = await http.put(
      `/buildings/${buildingId}/payments/fund/emergency`,
      { amount, note },
    );
    return data;
  },

  async receipt(buildingId, chargeId) {
    requireBuilding(buildingId);
    const { data } = await http.post(
      `/buildings/${buildingId}/payments/${chargeId}/receipt`,
      {},
    );
    return data;
  },

  async countCash(buildingId, amount, note) {
    requireBuilding(buildingId);
    const { data } = await http.post(`/buildings/${buildingId}/payments/fund/count`, {
      amount,
      note,
    });
    return data;
  },

  async payerPrivacy(buildingId) {
    requireBuilding(buildingId);
    const { data } = await http.get(`/buildings/${buildingId}/payments/privacy`);
    return data.showPayerNames;
  },

  async setPayerPrivacy(buildingId, showPayerNames) {
    requireBuilding(buildingId);
    const { data } = await http.put(`/buildings/${buildingId}/payments/privacy`, {
      showPayerNames,
    });
    return data.showPayerNames;
  },

  async transparencyVisibility(buildingId) {
    requireBuilding(buildingId);
    const { data } = await http.get(`/buildings/${buildingId}/payments/privacy`);
    return Boolean(data.transparencyPublic);
  },

  async setTransparencyVisibility(buildingId, isPublic) {
    requireBuilding(buildingId);
    const { data } = await http.put(`/buildings/${buildingId}/payments/privacy`, {
      transparencyPublic: isPublic,
    });
    return Boolean(data.transparencyPublic);
  },

  // #137: دفتر المصاريف وفواتيره للسكان — الحقل الثالث في نفس نقطة الخصوصية.
  async expensesVisibility(buildingId) {
    requireBuilding(buildingId);
    const { data } = await http.get(`/buildings/${buildingId}/payments/privacy`);
    return data.expensesVisibleToResidents !== false;
  },

  async setExpensesVisibility(buildingId, visible) {
    requireBuilding(buildingId);
    const { data } = await http.put(`/buildings/${buildingId}/payments/privacy`, {
      expensesVisibleToResidents: visible,
    });
    return Boolean(data.expensesVisibleToResidents);
  },

  async markPaid(buildingId, chargeId, body) {
    requireBuilding(buildingId);
    const { data } = await http.post(`/buildings/${buildingId}/payments/${chargeId}/pay`, body);
    return data;
  },

  async ledger(buildingId, chargeId) {
    requireBuilding(buildingId);
    const { data } = await http.get(`/buildings/${buildingId}/payments/${chargeId}/ledger`);
    return data;
  },

  async void(buildingId, paymentId, reason) {
    requireBuilding(buildingId);
    await http.post(`/buildings/${buildingId}/payments/entries/${paymentId}/void`, { reason });
  },

  async deletePayment(buildingId, paymentId) {
    requireBuilding(buildingId);
    await http.delete(`/buildings/${buildingId}/payments/entries/${paymentId}`);
  },

  async editAmount(buildingId, chargeId, amount) {
    requireBuilding(buildingId);
    const { data } = await http.put(`/buildings/${buildingId}/payments/${chargeId}/amount`, { amount });
    return data;
  },

  async regenerate(buildingId, year, month) {
    requireBuilding(buildingId);
    const { data } = await http.post(`/buildings/${buildingId}/payments/regenerate`, { year, month });
    return data;
  },

  async fundEntries(buildingId, fund = 'operating') {
    requireBuilding(buildingId);
    const { data } = await http.get(`/buildings/${buildingId}/payments/fund/entries`, { params: { fund } });
    return data;
  },

  async addFundEntry(buildingId, body) {
    requireBuilding(buildingId);
    const { data } = await http.post(`/buildings/${buildingId}/payments/fund/entries`, body);
    return data;
  },

  async voidFundEntry(buildingId, id, reason) {
    requireBuilding(buildingId);
    const { data } = await http.post(`/buildings/${buildingId}/payments/fund/entries/${id}/void`, { reason });
    return data;
  },

  async remind(buildingId, chargeId) {
    requireBuilding(buildingId);
    await http.post(`/buildings/${buildingId}/payments/${chargeId}/remind`);
  },

  async myMonths(buildingId, months = 12) {
    requireBuilding(buildingId);
    const { data } = await http.get(`/buildings/${buildingId}/payments/mine/months`, {
      params: { months },
    });
    return data;
  },

  async declareCash(buildingId, chargeId, body = {}) {
    requireBuilding(buildingId);
    const { data } = await http.post(
      `/buildings/${buildingId}/payments/${chargeId}/declare-cash`,
      body,
    );
    return data;
  },

  async remindLate(buildingId) {
    requireBuilding(buildingId);
    const { data } = await http.post(`/buildings/${buildingId}/payments/remind-late`);
    return data;
  },
};

/* --------------------------------------------------------------- expenses */

export const expensesApi: Contract['expensesApi'] = {
  async list(buildingId, year, month, page) {
    requireBuilding(buildingId);
    const { data } = await http.get(`/buildings/${buildingId}/expenses`, {
      params: params({ year, month, ...paging(page) }),
    });
    return data;
  },

  // #137: الدفتر الذي يقرؤه الساكن (مع الملغاة مشطوبة).
  async ledger(buildingId, year, month, page) {
    requireBuilding(buildingId);
    const { data } = await http.get(`/buildings/${buildingId}/expenses/ledger`, {
      params: params({ year, month, ...paging(page) }),
    });
    return data;
  },

  async create(buildingId, body) {
    requireBuilding(buildingId);
    const { data } = await http.post(`/buildings/${buildingId}/expenses`, body);
    return data;
  },

  async void(buildingId, id, reason) {
    requireBuilding(buildingId);
    const { data } = await http.post(`/buildings/${buildingId}/expenses/${id}/void`, { reason });
    return data;
  },
};

/* ---------------------------------------------------------------- tickets */

export const ticketsApi: Contract['ticketsApi'] = {
  async list(buildingId, status?: TicketStatus, page?: PageQuery) {
    requireBuilding(buildingId);
    const { data } = await http.get(`/buildings/${buildingId}/tickets`, {
      params: params({ status, ...paging(page) }),
    });
    return data;
  },

  async create(buildingId, body) {
    requireBuilding(buildingId);
    const { data } = await http.post(`/buildings/${buildingId}/tickets`, body);
    return data;
  },

  async close(buildingId, id, body) {
    requireBuilding(buildingId);
    const { data } = await http.post(`/buildings/${buildingId}/tickets/${id}/close`, body);
    return data;
  },

  async meToo(buildingId, id) {
    requireBuilding(buildingId);
    const { data } = await http.post(`/buildings/${buildingId}/tickets/${id}/me-too`);
    return data;
  },

  async meTooUndo(buildingId, id) {
    requireBuilding(buildingId);
    const { data } = await http.delete(`/buildings/${buildingId}/tickets/${id}/me-too`);
    return data;
  },
};

/* ---------------------------------------------------------------- members */

export const membersApi: Contract['membersApi'] = {
  async createFromUnits(buildingId, withPasswords = false) {
    requireBuilding(buildingId);
    const { data } = await http.post(`/buildings/${buildingId}/members/from-units`, null, {
      params: { withPasswords },
    });
    return data;
  },
  async list(buildingId, page) {
    requireBuilding(buildingId);
    const { data } = await http.get(`/buildings/${buildingId}/members`, {
      params: params(paging(page)),
    });
    return data;
  },

  async add(buildingId, body) {
    requireBuilding(buildingId);
    const { data } = await http.post(`/buildings/${buildingId}/members`, body);
    return data;
  },

  async unlock(buildingId, memberId) {
    requireBuilding(buildingId);
    const { data } = await http.post(`/buildings/${buildingId}/members/${memberId}/unlock`);
    return data;
  },

  async update(buildingId, memberId, body) {
    requireBuilding(buildingId);
    const { data } = await http.put(`/buildings/${buildingId}/members/${memberId}`, body);
    return data;
  },

  async deactivate(buildingId, memberId) {
    requireBuilding(buildingId);
    await http.post(`/buildings/${buildingId}/members/${memberId}/deactivate`);
  },
};

/* ------------------------------------------------------------ attachments */

export const uploadsApi: Contract['uploadsApi'] = {
  async list(buildingId, entityType: AttachmentEntity, entityId: number) {
    requireBuilding(buildingId);
    const { data } = await http.get(`/buildings/${buildingId}/attachments`, {
      params: params({ entityType, entityId }),
    });
    return data;
  },

  async upload(buildingId, entityType, entityId, file) {
    requireBuilding(buildingId);
    const form = new FormData();
    form.append('entityType', entityType);
    form.append('entityId', String(entityId));
    form.append('file', file);

    // Let the browser set the multipart boundary; a hard-coded header breaks it.
    const { data } = await http.post<Attachment>(`/buildings/${buildingId}/attachments`, form, {
      headers: { 'Content-Type': undefined },
    });
    return data;
  },

  async remove(buildingId, id) {
    requireBuilding(buildingId);
    await http.delete(`/buildings/${buildingId}/attachments/${id}`);
  },
};

/* ---------------------------------------------------------- subscriptions */

export const subscriptionsApi: Contract['subscriptionsApi'] = {
  async get(buildingId) {
    requireBuilding(buildingId);
    const { data } = await http.get(`/buildings/${buildingId}/subscription`);
    return data;
  },

  async setPlan(buildingId, plan: SubscriptionPlan) {
    requireBuilding(buildingId);
    const { data } = await http.put(`/admin/buildings/${buildingId}/subscription`, { plan });
    return data;
  },
};

/* ----------------------------------------------------------- transparency */

export const transparencyApi: Contract['transparencyApi'] = {
  // Deliberately unauthenticated: totals only, no names, no phone numbers.
  async get(publicId, period) {
    const { data } = await http.get(`/public/transparency/${encodeURIComponent(publicId)}`, {
      params: period ? params({ year: period.year, month: period.month }) : undefined,
    });
    return data;
  },
};

/* ------------------------------------------------------- notifications */

export const notificationsApi: Contract['notificationsApi'] = {
  async feed(page) {
    const { data } = await http.get('/notifications', { params: params(paging(page)) });
    return data;
  },

  async markRead(id) {
    await http.post(`/notifications/${id}/read`);
  },

  async markAllRead() {
    await http.post('/notifications/read-all');
  },
};

/* ------------------------------------------------------- announcements */

export const announcementsApi: Contract['announcementsApi'] = {
  async remove(buildingId, id) {
    requireBuilding(buildingId);
    await http.delete(`/buildings/${buildingId}/announcements/${id}`);
  },
  async list(buildingId, page) {
    requireBuilding(buildingId);
    const { data } = await http.get(`/buildings/${buildingId}/announcements`, {
      params: params(paging(page)),
    });
    return data;
  },

  async create(buildingId, body) {
    requireBuilding(buildingId);
    const { data } = await http.post(`/buildings/${buildingId}/announcements`, body);
    return data;
  },
};

/* -------------------------------------------------- platform user admin */

export const platformUsersApi: Contract['platformUsersApi'] = {
  async create(body) {
    const { data } = await http.post('/admin/users', body);
    return data;
  },

  async update(userId, body) {
    const { data } = await http.put(`/admin/users/${userId}`, body);
    return data;
  },

  async linkUnit(userId, body) {
    const { data } = await http.put(`/admin/users/${userId}/unit`, body);
    return data;
  },

  async importTemplate() {
    const { data } = await http.get('/admin/users/import/template', { responseType: 'blob' });
    return data as Blob;
  },

  async importAccounts(file) {
    const form = new FormData();
    form.append('file', file);
    const { data } = await http.post('/admin/users/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  // No delete endpoint exists, by design: the account is attached to financial
  // history that must stay countable.
  async setStatus(userId, body) {
    const { data } = await http.post(`/admin/users/${userId}/status`, body);
    return data;
  },
};

/* ------------------------------------------------------------ billing */

export const billingApi: Contract['billingApi'] = {
  async summary() {
    const { data } = await http.get('/admin/billing/summary');
    return data;
  },

  async list(search, page) {
    const { data } = await http.get('/admin/billing', {
      params: params({ search, ...paging(page) }),
    });
    return data;
  },

  async get(buildingId) {
    const { data } = await http.get(`/admin/billing/${buildingId}`);
    return data;
  },

  async update(buildingId, body) {
    const { data } = await http.put(`/admin/billing/${buildingId}`, body);
    return data;
  },

  async plans() {
    const { data } = await http.get('/admin/billing/plans');
    return data;
  },

  async setStatus(buildingId, body) {
    const { data } = await http.post(`/admin/billing/${buildingId}/status`, body);
    return data;
  },

  async remind(buildingId) {
    const { data } = await http.post(`/admin/billing/${buildingId}/remind`);
    return data;
  },
};

/* ---------------------------------------------------- bulk import */

export const importApi: Contract['importApi'] = {
  async template(kind) {
    // The server builds a real .xlsx with openpyxl; the browser only saves it.
    const { data } = await http.get('/imports/template', {
      params: params({ kind }),
      responseType: 'blob',
    });
    return data as Blob;
  },

  async preview(buildingId, kind, file) {
    requireBuilding(buildingId);
    const form = new FormData();
    form.append('kind', kind);
    form.append('file', file);

    const { data } = await http.post(`/buildings/${buildingId}/imports/preview`, form, {
      headers: { 'Content-Type': undefined },
    });
    return data;
  },

  async commit(buildingId, token, skipErrors) {
    requireBuilding(buildingId);
    const { data } = await http.post(`/buildings/${buildingId}/imports/commit`, {
      token,
      skipErrors,
    });
    return data;
  },
};

/* -------------------------------------------------------- sales tax */

export const taxApi: Contract['taxApi'] = {
  async get() {
    const { data } = await http.get('/admin/tax');
    return data;
  },
  async update(body) {
    const { data } = await http.put('/admin/tax', body);
    return data;
  },
};

/* ---------------------------------------------------- message quotas */

export const quotaApi: Contract['quotaApi'] = {
  async plans() {
    const { data } = await http.get('/admin/quotas');
    return data;
  },
  async usage(buildingId) {
    requireBuilding(buildingId);
    const { data } = await http.get(`/buildings/${buildingId}/messages/usage`);
    return data;
  },
};

/* ------------------------------------------------------ email identity */

export const emailApi: Contract['emailApi'] = {
  async status() {
    const { data } = await http.get('/account/email');
    return data;
  },
  async setEmail(email) {
    const { data } = await http.put('/account/email', { email });
    return data;
  },
  async verify(code) {
    const { data } = await http.post('/account/email/verify', { code });
    return data;
  },
  async setLoginEnabled(enabled) {
    const { data } = await http.put('/account/email/login', { enabled });
    return data;
  },
  async remove() {
    const { data } = await http.delete('/account/email');
    return data;
  },
};

/* ------------------------------------------- public site content & plans */

export const siteApi: Contract['siteApi'] = {
  // Unauthenticated: the landing page needs prices before anyone signs in.
  async content() {
    const { data } = await http.get('/public/site');
    return data;
  },
};

export const siteAdminApi: Contract['siteAdminApi'] = {
  async createPlan(body) {
    const { data } = await http.post('/admin/site/plans', body);
    return data;
  },
  async retirePlan(code) {
    const { data } = await http.delete(`/admin/site/plans/${code}`);
    return data;
  },
  async get() {
    const { data } = await http.get('/admin/site');
    return data;
  },
  async update(body) {
    const { data } = await http.put('/admin/site', body);
    return data;
  },
  async plans() {
    const { data } = await http.get('/admin/site/plans');
    return data;
  },
  async updatePlan(code, body) {
    const { data } = await http.put(`/admin/site/plans/${code}`, body);
    return data;
  },
};

/* --------------------------------------------- profile self-service */

export const profileApi: Contract['profileApi'] = {
  async update(body) {
    const { data } = await http.put('/account/profile', body);
    return data;
  },
  async phoneStatus() {
    const { data } = await http.get('/account/phone');
    return data;
  },
  async requestPhoneChange(body) {
    const { data } = await http.post('/account/phone/request', body);
    return data;
  },
  async confirmPhoneChange(code) {
    const { data } = await http.post('/account/phone/confirm', { code });
    return data;
  },
  async cancelPhoneChange() {
    const { data } = await http.delete('/account/phone');
    return data;
  },
};

/* ------------------------------------------------- collection reporting */

export const reportsApi: Contract['reportsApi'] = {
  async collection(buildingId, year, month) {
    requireBuilding(buildingId);
    const { data } = await http.get(`/buildings/${buildingId}/reports/collection`, {
      params: params({ year, month }),
    });
    return data;
  },

  async cliqEntries(buildingId, year, month, page) {
    requireBuilding(buildingId);
    const { data } = await http.get(`/buildings/${buildingId}/reports/transfers`, {
      params: params({ year, month, ...paging(page) }),
    });
    return data;
  },

  async platformCollection(year, month) {
    const { data } = await http.get('/admin/reports/collection', {
      params: params({ year, month }),
    });
    return data;
  },
};

/* ------------------------------------------------- building onboarding */

export const onboardingApi: Contract['onboardingApi'] = {
  async createBuilding(body) {
    const { data } = await http.post('/admin/buildings', body);
    return data;
  },
};

/* ------------------------------------------- تطبيق المصادقة (TOTP) */

export const totpApi: Contract['totpApi'] = {
  async status() {
    const { data } = await http.get('/account/totp');
    return data;
  },
  async setup() {
    const { data } = await http.post('/account/totp/setup');
    return data;
  },
  async confirm(code) {
    const { data } = await http.post('/account/totp/confirm', { code });
    return data;
  },
  // مخرج من فقد تطبيقه: رمز إلى هاتفه بدل رمز التطبيق.
  async recoverOtp() {
    await http.post('/account/totp/recover-otp');
  },
  async disableWithOtp(otp) {
    const { data } = await http.post('/account/totp/disable', { otp });
    return data;
  },
  // مخرج لا يعتمد على مزوّد رسائل: كلمة السر إثبات هوية يملكه صاحب
  // الحساب ولا يحتاج شبكة.
  async disableWithPassword(password) {
    const { data } = await http.post('/account/totp/disable', { password });
    return data;
  },
  async disable(code) {
    const { data } = await http.post('/account/totp/disable', { code });
    return data;
  },
};

export const loginCodeApi: Contract['loginCodeApi'] = {
  async issue(userId) {
    const { data } = await http.post(`/admin/users/${userId}/login-code`);
    return data;
  },
};

export const lifetimeApi: Contract['lifetimeApi'] = {
  // المدى يُرسل باسمه ويُحسب على الخادم بتوقيت عمّان. حساب الحدود هنا
  // يعني أن «الشهر الماضي» يختلف باختلاف توقيت جهاز المستخدم.
  async building(buildingId, range = 'all') {
    requireBuilding(buildingId);
    const { data } = await http.get(`/buildings/${buildingId}/reports/lifetime`, {
      params: params({ range }),
    });
    return data;
  },
  async platform(range = 'all') {
    const { data } = await http.get('/admin/reports/lifetime', {
      params: params({ range }),
    });
    return data;
  },
  async setWebsite(buildingId, websiteUrl) {
    requireBuilding(buildingId);
    const { data } = await http.put(`/buildings/${buildingId}/website`, { websiteUrl });
    return data;
  },
  async website(buildingId) {
    requireBuilding(buildingId);
    const { data } = await http.get(`/buildings/${buildingId}`);
    return { websiteUrl: data.websiteUrl ?? null };
  },
};

/* --------------------------------------------- تتبّع الموقع */

export const observabilityApi: Contract['observabilityApi'] = {
  async audit(query, page) {
    const { data } = await http.get('/admin/observability/audit', {
      params: params({ ...query, ...paging(page) }),
    });
    return data;
  },
  async facets() {
    const { data } = await http.get('/admin/observability/facets');
    return data;
  },
  async activity(days = 14) {
    const { data } = await http.get('/admin/observability/activity', {
      params: params({ days }),
    });
    return data;
  },
  async health() {
    const { data } = await http.get('/admin/observability/health');
    return data;
  },
};

/* ------------------------------------- كلمة السر والمصادقة الثنائية */

export const passwordApi: Contract['passwordApi'] = {
  async login(phone, password) {
    const { data } = await http.post('/auth/password', { phone, password });
    return data;
  },
  async changeOwn(currentPassword, newPassword) {
    await http.put('/account/password', { currentPassword, newPassword });
  },
  async setForUser(userId, password) {
    await http.post(`/admin/users/${userId}/password`, { password });
  },
  async clearForUser(userId) {
    await http.delete(`/admin/users/${userId}/password`);
  },
  async twoFactor() {
    const { data } = await http.get('/account/two-factor');
    return data;
  },
  async setTwoFactor(enabled, channel) {
    const { data } = await http.put('/account/two-factor', { enabled, channel });
    return data;
  },
};

export const authPolicyApi: Contract['authPolicyApi'] = {
  async get() {
    const { data } = await http.get('/admin/auth-policy');
    return data;
  },
  async update(body) {
    const { data } = await http.put('/admin/auth-policy', body);
    return data;
  },
};

/* --------------------------------------------- التسجيل الذاتي */

export const signupApi: Contract['signupApi'] = {
  async status() {
    const { data } = await http.get('/public/signup-status');
    return data;
  },
  async start(body) {
    const { data } = await http.post('/public/signup', body);
    return data;
  },
  async verify(requestId, code) {
    const { data } = await http.post('/public/signup/verify', { requestId, code });
    return data;
  },
  async list() {
    const { data } = await http.get('/admin/signups');
    return data;
  },
};

export const bulkPaymentApi: Contract['bulkPaymentApi'] = {
  async payMany(buildingId, body) {
    requireBuilding(buildingId);
    const { data } = await http.post(`/buildings/${buildingId}/payments/pay-many`, body);
    return data;
  },
};

/* ────────────────────── فواتير اشتراك المنصّة ────────────────────── */

export const platformBillingApi: Contract['platformBillingApi'] = {
  async issue(year, month) {
    const { data } = await http.post('/admin/billing/invoices/generate', null, {
      params: params({ year, month }),
    });
    return data;
  },

  async invoices(year, month, status, search, page) {
    const { data } = await http.get('/admin/billing/invoices', {
      params: params({ year, month, status: status || undefined, search, ...paging(page) }),
    });
    return data;
  },

  async report(year, month) {
    const { data } = await http.get('/admin/billing/report', {
      params: params({ year, month }),
    });
    return data;
  },

  async revenue(from, to) {
    const { data } = await http.get('/admin/billing/revenue', {
      params: params({ from: from ?? undefined, to: to ?? undefined }),
    });
    return data;
  },

  async send(invoiceId) {
    const { data } = await http.post(`/admin/billing/invoices/${invoiceId}/send`);
    return data;
  },

  async edit(invoiceId, body) {
    const { data } = await http.put(`/admin/billing/invoices/${invoiceId}`, body);
    return data;
  },

  async remove(invoiceId) {
    await http.delete(`/admin/billing/invoices/${invoiceId}`);
  },

  async pay(invoiceId, body) {
    const { data } = await http.post(`/admin/billing/invoices/${invoiceId}/pay`, body);
    return data;
  },

  async reset(invoiceId, reason, waive) {
    const { data } = await http.post(`/admin/billing/invoices/${invoiceId}/reset`, {
      reason,
      waive,
    });
    return data;
  },
};

/* ─────────── فواتير المنصّة كما يراها مسؤول العمارة ─────────── */

export const buildingInvoicesApi: Contract['buildingInvoicesApi'] = {
  async list(buildingId, page) {
    requireBuilding(buildingId);
    const { data } = await http.get(`/buildings/${buildingId}/invoices`, {
      params: params({ ...paging(page) }),
    });
    return data;
  },

  async get(buildingId, invoiceId) {
    requireBuilding(buildingId);
    const { data } = await http.get(`/buildings/${buildingId}/invoices/${invoiceId}`);
    return data;
  },

  async decideExpense(buildingId, invoiceId, decision) {
    requireBuilding(buildingId);
    const { data } = await http.post(`/buildings/${buildingId}/invoices/${invoiceId}/expense`, {
      decision,
    });
    return data;
  },
};

/* ─────────────────── التصويت ─────────────────── */

export const pollsApi: Contract['pollsApi'] = {
  async list(buildingId, status, page) {
    requireBuilding(buildingId);
    const { data } = await http.get(`/buildings/${buildingId}/polls`, {
      params: params({ status: status || undefined, ...paging(page) }),
    });
    return data;
  },

  async create(buildingId, body) {
    requireBuilding(buildingId);
    const { data } = await http.post(`/buildings/${buildingId}/polls`, body);
    return data;
  },

  async vote(buildingId, pollId, choice) {
    requireBuilding(buildingId);
    const { data } = await http.post(`/buildings/${buildingId}/polls/${pollId}/vote`, {
      choice,
    });
    return data;
  },

  async remove(buildingId, pollId) {
    requireBuilding(buildingId);
    await http.delete(`/buildings/${buildingId}/polls/${pollId}`);
  },

  async close(buildingId, pollId) {
    requireBuilding(buildingId);
    const { data } = await http.post(`/buildings/${buildingId}/polls/${pollId}/close`);
    return data;
  },
};

export const currencyApi: Contract['currencyApi'] = {
  async get(buildingId) {
    requireBuilding(buildingId);
    const { data } = await http.get(`/buildings/${buildingId}/settings/currency`);
    return data;
  },
  async set(buildingId, currency, minorUnits) {
    requireBuilding(buildingId);
    const { data } = await http.put(`/buildings/${buildingId}/settings/currency`, {
      currency,
      minorUnits,
    });
    return data;
  },
};

/* ─────────────────── التواصل والوصل ─────────────────── */

export const contactApi: Contract['contactApi'] = {
  async submit(body) {
    await http.post('/public/contact', body);
    return { ok: true };
  },
  async inbox(status, page) {
    const { data } = await http.get('/admin/contact', {
      params: params({ status: status || undefined, ...paging(page) }),
    });
    return data;
  },
  async update(id, status, adminNote) {
    const { data } = await http.put(`/admin/contact/${id}`, { status, adminNote });
    return data;
  },
};

export const receiptApi: Contract['receiptApi'] = {
  async read(token) {
    const { data } = await http.get(`/public/receipt/${token}`);
    return data;
  },
};

/* ─────────── تحكّم المنصّة: تصفير المصادقة ومدد الاحتفاظ ─────────── */

export const adminControlsApi: Contract['adminControlsApi'] = {
  async unlock(userId) {
    const { data } = await http.post(`/admin/users/${userId}/unlock`);
    return data;
  },

  async resetTotp(userId) {
    await http.post(`/admin/users/${userId}/reset-totp`);
  },
  async retention() {
    const { data } = await http.get('/admin/retention');
    return data;
  },
  async setRetention(body) {
    const { data } = await http.put('/admin/retention', body);
    return data;
  },
  async purgeNow() {
    const { data } = await http.post('/admin/retention/purge');
    return data;
  },
};

/* ─────────────────── التسعير وأكواد الخصم ─────────────────── */

export const promosApi: Contract['promosApi'] = {
  async list() {
    const { data } = await http.get('/admin/promos');
    return data.items;
  },
  async create(body) {
    const { data } = await http.post('/admin/promos', body);
    return data;
  },
  async update(id, body) {
    const { data } = await http.put(`/admin/promos/${id}`, body);
    return data;
  },
  async applyTo(buildingId, code) {
    const { data } = await http.post(`/admin/buildings/${buildingId}/promo`, { code });
    return data;
  },
  async publicPricing() {
    const { data } = await http.get('/public/pricing');
    return data;
  },
  // تحقّق قبل ملء النموذج: من ينسخ الكود من الصفحة الرئيسية يجب أن يعرف
  // أنه صالح قبل أن يعبّئ كل شيء ويُرفض.
  async check(code) {
    const { data } = await http.get(`/public/promo/${encodeURIComponent(code)}`);
    return data;
  },
};


export const messagesApi: Contract['messagesApi'] = {
  async send(buildingId, body) {
    requireBuilding(buildingId);
    const { data } = await http.post(`/buildings/${buildingId}/messages`, { body });
    return data;
  },
  async list(buildingId, page = {}) {
    requireBuilding(buildingId);
    const { data } = await http.get(`/buildings/${buildingId}/messages`, { params: params(paging(page)) });
    return data;
  },
  async markRead(buildingId, id) {
    requireBuilding(buildingId);
    const { data } = await http.post(`/buildings/${buildingId}/messages/${id}/read`);
    return data;
  },
};

export const floorsApi: Contract['floorsApi'] = {
  async get(buildingId) {
    requireBuilding(buildingId);
    const { data } = await http.get(`/buildings/${buildingId}/settings/floors`);
    return data;
  },
  async set(buildingId, floors) {
    requireBuilding(buildingId);
    const { data } = await http.put(`/buildings/${buildingId}/settings/floors`, floors);
    return data;
  },
};
